import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { IconFolder } from './Icons';

const ProjectsScreen = ({ apiBaseUrl, onOpen, onError }) => {
  const [projects, setProjects] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/api/projects`);
      if (response.data.success) {
        setProjects(response.data.projects || []);
        setIsAdmin(!!response.data.is_admin);
      } else {
        onError(response.data.error || 'Не удалось загрузить проекты');
      }
    } catch (err) {
      onError(err.response?.data?.error || 'Ошибка при загрузке проектов');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(`Удалить проект «${project.name}»?\nЭто действие необратимо.`)) return;
    setDeletingId(project.id);
    try {
      const response = await axios.delete(`${apiBaseUrl}/api/project/${project.id}`);
      if (response.data.success) {
        setProjects((prev) => prev.filter((p) => p.id !== project.id));
      } else {
        onError(response.data.error || 'Не удалось удалить проект');
      }
    } catch (err) {
      onError(err.response?.data?.error || 'Ошибка при удалении проекта');
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpen = async (project) => {
    setOpeningId(project.id);
    try {
      await onOpen(project.id);
    } finally {
      setOpeningId(null);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString('ru-RU');
  };

  const query = search.trim().toLowerCase();
  const filtered = projects.filter((p) => {
    if (!query) return true;
    const inName = (p.name || '').toLowerCase().includes(query);
    const inOwner = isAdmin && (p.owner_email || '').toLowerCase().includes(query);
    return inName || inOwner;
  });

  return (
    <div className="projects-screen">
      <div className="projects-header">
        <h2>
          <IconFolder size={20} style={{ marginRight: '8px', marginTop: '-3px' }} />Проекты
          {isAdmin && <span className="admin-badge">админ: все проекты</span>}
        </h2>
        <p className="subtitle">
          Открывайте сохранённые проекты, смотрите визуализацию и экспортируйте результаты.
        </p>
      </div>

      <div className="projects-toolbar">
        <input
          type="text"
          className="projects-search"
          placeholder="Поиск по названию…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="secondary-button" onClick={loadProjects} disabled={loading}>
          {loading ? 'Обновление…' : '↻ Обновить'}
        </button>
        <span className="projects-count">Всего: {filtered.length}</span>
      </div>

      {loading ? (
        <div className="projects-empty">Загрузка проектов…</div>
      ) : filtered.length === 0 ? (
        <div className="projects-empty">Проекты не найдены.</div>
      ) : (
        <div className="projects-table-wrapper">
          <table className="projects-table">
            <thead>
              <tr>
                <th>Название</th>
                {isAdmin && <th>Владелец</th>}
                <th>Текстов</th>
                <th>Кластеров</th>
                <th>Статус</th>
                <th>Создан</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id}>
                  <td className="proj-name">{p.name}</td>
                  {isAdmin && <td>{p.owner_email || '—'}</td>}
                  <td>{p.text_count}</td>
                  <td>{p.n_clusters || '—'}</td>
                  <td>
                    {p.has_categories ? (
                      <span className="badge badge-cat">категории</span>
                    ) : p.has_clusters ? (
                      <span className="badge badge-clu">кластеры</span>
                    ) : (
                      <span className="badge badge-empty">нет анализа</span>
                    )}
                  </td>
                  <td>{formatDate(p.created_at)}</td>
                  <td className="proj-actions">
                    <button
                      className="primary-button btn-sm"
                      onClick={() => handleOpen(p)}
                      disabled={!p.has_clusters || openingId === p.id}
                      title={
                        !p.has_clusters
                          ? 'В проекте нет результатов кластеризации'
                          : 'Открыть проект'
                      }
                    >
                      {openingId === p.id ? 'Открываю…' : 'Открыть'}
                    </button>
                    <button
                      className="danger-button btn-sm"
                      onClick={() => handleDelete(p)}
                      disabled={deletingId === p.id}
                    >
                      {deletingId === p.id ? 'Удаляю…' : 'Удалить'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectsScreen;
